import {
  RELAYED_TURN_COMMANDS,
  type MobileRelayPort,
  type RelayedTranscriptTurn,
  type RelayedTurnCommand,
} from '@/shared-responder-v2-core';
import type { UtRemoteVoiceTurn, VoiceCommandEvent } from '@/shared-survey';

/**
 * The three mobile-UT mutations, named by what they do rather than by the
 * Apollo hook that binds them, so the runtime below is testable without one.
 */
interface MobileUtSessionGateway {
  readonly syncRecordingStarted: (uniqueId: string) => Promise<unknown>;
  readonly linkAudio: (args: {
    insightId: string;
    uniqueId: string;
  }) => Promise<unknown>;
  readonly endSession: (args: {
    uniqueId: string;
    reason: string;
    redirectUrl: string | null;
  }) => Promise<unknown>;
}

const RELAYED_COMMANDS_BY_VOICE_COMMAND: Record<
  VoiceCommandEvent['type'],
  RelayedTurnCommand
> = {
  next_question: RELAYED_TURN_COMMANDS.Next,
  submit_answer: RELAYED_TURN_COMMANDS.Submit,
};

export function toRelayedTranscriptTurn(
  turn: UtRemoteVoiceTurn
): RelayedTranscriptTurn {
  return {
    transcript: turn.transcript,
    isFinal: turn.isFinal,
    command:
      turn.command === null
        ? null
        : RELAYED_COMMANDS_BY_VOICE_COMMAND[turn.command.type],
  };
}

export interface MobileRelayRuntime {
  /** Fans one relayed turn out to every subscriber, kernel-side shape. */
  readonly emitTurn: (turn: UtRemoteVoiceTurn) => void;
  /**
   * The port for a given phone. Rebuilt when the `uniqueId` changes because
   * consumers read that field to decide whether the session is relayed at all
   * — a value behind a getter on a permanently stable object would be read
   * once and never again.
   */
  readonly portFor: (uniqueId: string | null) => MobileRelayPort;
}

/**
 * Holds the subscriber list and the session mutations for the life of the
 * page, so the port handed to the responder is a plain object rather than one
 * rebuilt on every render — the kernel keeps it in an effect's dependencies.
 *
 * Every mutation is inert without a `uniqueId`: a session on the responder's
 * own device has no phone to sync with, and the same port serves it.
 */
export function createMobileRelayRuntime(
  gateway: MobileUtSessionGateway
): MobileRelayRuntime {
  const listeners = new Set<(turn: RelayedTranscriptTurn) => void>();

  return {
    emitTurn: (turn) => {
      const relayed = toRelayedTranscriptTurn(turn);

      listeners.forEach((listener) => listener(relayed));
    },
    portFor: (uniqueId) => ({
      uniqueId,
      subscribe: (listener) => {
        listeners.add(listener);

        return () => {
          listeners.delete(listener);
        };
      },
      syncRecordingStarted: async () => {
        if (uniqueId === null) return;

        await gateway.syncRecordingStarted(uniqueId);
      },
      linkAudio: async (insightId) => {
        if (uniqueId === null) return;

        await gateway.linkAudio({ insightId, uniqueId });
      },
      endSession: async ({ reason, redirectUrl }) => {
        if (uniqueId === null) return;

        await gateway.endSession({ uniqueId, reason, redirectUrl });
      },
    }),
  };
}
